import {
    ReadonlyMat3,
    ReadonlyMat4,
    ReadonlyVec2,
    ReadonlyVec3,
    vec3,
} from 'gl-matrix';
import { Vertex } from './slicemesh/Vertex';

export class BoundingBox {
    public min?: vec3;
    public max?: vec3;

    public constructor();

    public constructor(ref: BoundingBox);

    public constructor(point: ReadonlyVec3);

    public constructor(point: ReadonlyVec2);

    public constructor(min: ReadonlyVec3, max: ReadonlyVec3);

    public constructor(
        min?: ReadonlyVec3 | ReadonlyVec2 | BoundingBox,
        max?: ReadonlyVec3
    ) {
        if (min instanceof BoundingBox) {
            this.min = min.min ? vec3.clone(min.min) : undefined;
            this.max = min.max ? vec3.clone(min.max) : undefined;
        } else if (min !== undefined) {
            if (min.length === 3) {
                this.min = vec3.clone(min as vec3);
            } else {
                this.min = vec3.fromValues(min[0], min[1], 0);
            }
            if (max !== undefined) {
                this.max = vec3.clone(max);
            } else {
                //with single point max = min
                this.max = vec3.clone(this.min);
            }
        }
    }

    public get isEmpty() {
        return this.min === undefined;
    }

    public expandWithPoint(point: ReadonlyVec3): boolean;

    public expandWithPoint(x: number, y: number, z: number): boolean;

    public expandWithPoint(
        x: number | ReadonlyVec3,
        y?: number,
        z?: number
    ): boolean {
        let expanded = false;
        if (!this.min || !this.max) {
            if (x instanceof Object) {
                this.min = vec3.clone(x);
            } else {
                this.min = vec3.fromValues(x, y as number, z as number);
            }
            this.max = vec3.clone(this.min);
            expanded = true;
        } else if (x instanceof Object) {
            for (let i = 0; i < 3; ++i) {
                if (x[i] <= this.min[i]) {
                    this.min[i] = x[i];
                    expanded = true;
                } else if (x[i] >= this.max[i]) {
                    this.max[i] = x[i];
                    expanded = true;
                }
            }
        } else {
            if (x <= this.min[0]) {
                this.min[0] = x;
                expanded = true;
            } else if (x >= this.max[0]) {
                this.max[0] = x;
                expanded = true;
            }
            if ((y as number) <= this.min[1]) {
                this.min[1] = y as number;
                expanded = true;
            } else if ((y as number) >= this.max[1]) {
                this.max[1] = y as number;
                expanded = true;
            }
            if ((z as number) <= this.min[2]) {
                this.min[2] = z as number;
                expanded = true;
            } else if ((z as number) >= this.max[2]) {
                this.max[2] = z as number;
                expanded = true;
            }
        }
        return expanded;
    }

    public expandWithPoints(collection: Iterable<ReadonlyVec3>): boolean {
        let expanded = false;
        for (const vertex of collection) {
            expanded = this.expandWithPoint(vertex) || expanded;
        }
        return expanded;
    }

    expandWithPointsLine(points: number[][]) {
        points.forEach((p) => this.expandWithPoint(p[0], p[1], p[2]));
    }

    public isPointInside(
        ...args: [point: ReadonlyVec3] | [x: number, y: number, z: number]
    ): boolean {
        if (this.min && this.max) {
            if (args.length === 1) {
                const [point] = args;
                return (
                    point[0] > this.min[0] &&
                    point[0] < this.max[0] &&
                    point[1] > this.min[1] &&
                    point[1] < this.max[1] &&
                    point[2] > this.min[2] &&
                    point[2] < this.max[2]
                );
            } else if (args.length === 3) {
                const [x, y, z] = args;
                return (
                    x > this.min[0] &&
                    x < this.max[0] &&
                    y > this.min[1] &&
                    y < this.max[1] &&
                    z > this.min[2] &&
                    z < this.max[2]
                );
            }
        }
        return false;
    }

    public contains(bb: BoundingBox): boolean {
        if (!this.min || !this.max) {
            return false;
        } else if (!bb.min || !bb.max) {
            return true;
        } else {
            return (
                bb.min[0] >= this.min[0] &&
                bb.max[0] <= this.max[0] &&
                bb.min[1] >= this.min[1] &&
                bb.max[1] <= this.max[1] &&
                bb.min[2] >= this.min[2] &&
                bb.max[2] <= this.max[2]
            );
        }
    }

    public intersects(bb: BoundingBox): boolean {
        if (!this.min || !this.max || !bb.min || !bb.max) {
            return false;
        }

        return (
            this.max[0] >= bb.min[0] &&
            this.min[0] <= bb.max[0] &&
            this.max[1] >= bb.min[1] &&
            this.min[1] <= bb.max[1] &&
            this.max[2] >= bb.min[2] &&
            this.min[2] <= bb.max[2]
        );
    }

    public addMargins(margin: number): BoundingBox;

    public addMargins(
        minMargin: ReadonlyVec3,
        maxMargin?: ReadonlyVec3
    ): BoundingBox;

    public addMargins(xMin: number, yMin: number, zMin: number): BoundingBox;

    public addMargins(
        xMin: number,
        yMin: number,
        zMin: number,
        xMax: number,
        yMax: number,
        zMax: number
    ): BoundingBox;

    public addMargins(
        xMin: number | ReadonlyVec3,
        yMin?: number | ReadonlyVec3,
        zMin?: number,
        xMax?: number,
        yMax?: number,
        zMax?: number
    ): BoundingBox {
        let minMargin: vec3;
        let maxMargin: vec3;
        if (!this.min || !this.max) {
            return this;
        }
        if (xMin instanceof Object) {
            minMargin = vec3.clone(xMin);
            maxMargin = yMin
                ? vec3.clone(yMin as ReadonlyVec3)
                : vec3.clone(minMargin);
        } else {
            if (yMin !== undefined) {
                minMargin = vec3.fromValues(
                    xMin,
                    yMin as number,
                    zMin as number
                );
                if (xMax !== undefined) {
                    maxMargin = vec3.fromValues(
                        xMax,
                        yMax as number,
                        zMax as number
                    );
                } else {
                    maxMargin = vec3.clone(minMargin);
                }
            } else {
                minMargin = vec3.fromValues(xMin, xMin, xMin);
                maxMargin = vec3.clone(minMargin);
            }
        }
        vec3.subtract(this.min, this.min, minMargin);
        vec3.add(this.max, this.max, maxMargin);
        return this;
    }

    public set(other: BoundingBox): BoundingBox {
        if (!this.min) {
            this.min = other.min ? vec3.clone(other.min) : undefined;
        } else if (other.min) {
            vec3.copy(this.min, other.min);
        } else {
            this.min = undefined;
        }

        if (!this.max) {
            this.max = other.max ? vec3.clone(other.max) : undefined;
        } else if (other.max) {
            vec3.copy(this.max, other.max);
        } else {
            this.max = undefined;
        }
        return this;
    }

    /**
     * @deprecated
     * Needs to be rewritten to consider RotatedBoundingBox
     * @param other
     * @returns new BoundingBox which covers both of the source boundingBoxes.
     */
    public static union(bb1: BoundingBox, bb2: BoundingBox): BoundingBox {
        if (!bb1.min || !bb1.max) {
            if (!bb2.min || !bb2.max) {
                return new BoundingBox();
            } else {
                return new BoundingBox(bb2);
            }
        } else if (!bb2.min || !bb2.max) {
            return new BoundingBox(bb1);
        } else {
            const min = vec3.create();
            const max = vec3.create();
            vec3.min(min, bb1.min, bb2.min);
            vec3.max(max, bb1.max, bb2.max);
            return new BoundingBox(min, max);
        }
    }

    /**
     * Needs to be rewritten to consider RotatedBoundingBox
     * @deprecated
     * @param bb1
     * @param bb2
     * @returns
     */
    public static intersection(
        bb1: BoundingBox,
        bb2: BoundingBox
    ): BoundingBox {
        if (!bb1.min || !bb1.max || !bb2.min || !bb2.max) {
            return new BoundingBox();
        } else {
            const min = vec3.create();
            const max = vec3.create();
            vec3.max(min, bb1.min, bb2.min);
            vec3.min(max, bb1.max, bb2.max);
            return new BoundingBox(min, max);
        }
    }

    /**
     * @returns The size of this bounding box.
     */
    public get size(): ReadonlyVec3 {
        const size = vec3.create();
        if (this.max && this.min) {
            vec3.subtract(size, this.max, this.min);
            size[0] = size[0] < 0 ? -size[0] : size[0];
            size[1] = size[1] < 0 ? -size[1] : size[1];
            size[2] = size[2] < 0 ? -size[2] : size[2];
        }
        return size;
    }

    public get volume(): number {
        const size = this.size;
        return (size[0] || 1) * (size[1] || 1) * (size[2] || 1);
    }

    /**
     * Method to get the center point of this BoundingBox.
     * Center point coordinates are (min + max) / 2 if min and max are defined, (0, 0, 0) otherwise.
     * Note that these coordinates are in an arbitrary space, not necessarily PatientSpace.
     */
    public get center(): ReadonlyVec3 {
        if (!this.min || !this.max) {
            return vec3.create();
        } else {
            const size = vec3.add(vec3.create(), this.min, this.max);
            return vec3.scale(size, size, 0.5);
        }
    }

    translate(translation: ReadonlyVec3) {
        if (this.isEmpty) {
            throw new Error('Cannot translate an empty bounding box');
        }
        vec3.add(this.min as vec3, this.min as vec3, translation);
        vec3.add(this.max as vec3, this.max as vec3, translation);
    }

    scale(amount: number) {
        if (amount < 0) {
            throw new Error(
                'Trying to scale bounding box by a negative number'
            );
        }
        if (this.isEmpty) {
            throw new Error('Cannot scale an empty bounding box');
        }
        vec3.scale(this.min as vec3, this.min as vec3, amount);
        vec3.scale(this.max as vec3, this.max as vec3, amount);
    }

    /**
     * Method to get 8 vertices representing each of the {@link BoundingBox}'s corners.\
     * These are PatientSpace {@link vec3 coordinates}.
     * @param transformation Optional {@link ReadonlyMat3} or {@link ReadonlyMat4} transformation to be applied to each vertex.
     * @returns Array of 8 vertices representing the {@link BoundingBox}'s corners.
     */
    public getVertices(transformation?: ReadonlyMat3 | ReadonlyMat4): vec3[] {
        if (!this.min || !this.max) {
            return [];
        }
        const vertices = [
            vec3.clone(this.min),
            vec3.fromValues(this.max[0], this.min[1], this.min[2]),
            vec3.fromValues(this.max[0], this.max[1], this.min[2]),
            vec3.fromValues(this.min[0], this.max[1], this.min[2]),
            vec3.fromValues(this.min[0], this.min[1], this.max[2]),
            vec3.fromValues(this.max[0], this.min[1], this.max[2]),
            vec3.fromValues(this.max[0], this.max[1], this.max[2]),
            vec3.fromValues(this.min[0], this.max[1], this.max[2]),
        ];
        if (transformation) {
            if (transformation.length === 9) {
                for (const v of vertices) {
                    vec3.transformMat3(v, v, transformation);
                }
            } else {
                for (const v of vertices) {
                    vec3.transformMat4(v, v, transformation as ReadonlyMat4);
                }
            }
        }
        return vertices;
    }

    /**
     * Method to get the 8 {@link Vertex Vertices} of the {@link BoundingBox} with texture coordinates.\
     * These are PatientSpace {@link vec3 coordinates}.
     * @param transformation Optional {@link ReadonlyMat3} or {@link ReadonlyMat4} transformation to be {@link Vertex applied} to each {@link Vertex}.
     * @returns Array of 8 {@link Vertex Vertices} representing the {@link BoundingBox}'s corners.
     */
    public getVerticesWithTexture(
        transformation?: ReadonlyMat3 | ReadonlyMat4,
        textureSliceCount?: number
    ): Vertex[] {
        if (!this.min || !this.max) {
            return [];
        }
        // texture coordinates are in the middle of the slice
        const textureKcoord = 0.5 / (textureSliceCount ?? this.size[2]);
        const coordinates = [
            // bottom
            [this.min, vec3.fromValues(0, 0, textureKcoord)],
            [
                vec3.fromValues(this.min[0], this.max[1], this.min[2]),
                vec3.fromValues(0, 1, textureKcoord),
            ],
            [
                vec3.fromValues(this.max[0], this.max[1], this.min[2]),
                vec3.fromValues(1, 1, textureKcoord),
            ],
            [
                vec3.fromValues(this.max[0], this.min[1], this.min[2]),
                vec3.fromValues(1, 0, textureKcoord),
            ],
            // top
            [
                vec3.fromValues(this.min[0], this.min[1], this.max[2]),
                vec3.fromValues(0, 0, 1 - textureKcoord),
            ],
            [
                vec3.fromValues(this.min[0], this.max[1], this.max[2]),
                vec3.fromValues(0, 1, 1 - textureKcoord),
            ],
            [
                vec3.fromValues(this.max[0], this.max[1], this.max[2]),
                vec3.fromValues(1, 1, 1 - textureKcoord),
            ],
            [
                vec3.fromValues(this.max[0], this.min[1], this.max[2]),
                vec3.fromValues(1, 0, 1 - textureKcoord),
            ],
        ];
        let vertices: Vertex[];
        if (transformation) {
            vertices = coordinates.map((coords: Array<vec3>) => {
                return new Vertex(coords[0], coords[1]).transform(
                    transformation
                );
            });
        } else {
            vertices = coordinates.map((coords: Array<vec3>) => {
                return new Vertex(coords[0], coords[1]);
            });
        }
        return vertices;
    }

    /**
     * @override
     *
     * Method to get string presentation of a bounding box
     */
    public toString(): string {
        if (this.min && this.max) {
            return this.min.toString() + ' to ' + this.max.toString();
        } else {
            return '';
        }
    }

    /**
     *
     * @param bb1 BoundingBox that is being expanded.
     * @param bb2 BoundingBox that is to be added to first bounding box.
     * @returns Combination of the two boundingboxes if bb1 doesn't fit bb2 within. False if new bounding box doesn't need to be created.
     */
    /*public static combinedROIs(roi1: RegionOfInterest, roi2: RegionOfInterest): {rotation: Matrix3f, bb: BoundingBox} | false {
        //using the sequence's bb as if we use texture's bb, we won't have room for margins
        //TODO: Should probably change to texture's boundingBox?
        const bb1 = roi1.sequence.getNormalAlignedBoundingBox();
        if(bb1.isEmpty()){
            return {rotation: roi2.sequence.getRotation(), bb: roi2.sequence.getNormalAlignedBoundingBox()};
        }
        const invRotation = new Matrix3f(roi1.sequence.getRotation());
        const destToPatient = new Matrix4f(roi1.getTransformationMatrix()).multiply(invRotation);
        const patientToDest = new Matrix4f(destToPatient).invert();
        const srcToDest = new Matrix4f(patientToDest);
        const srcToPatient = roi2.getTransformationMatrix();

        if(srcToPatient){
            srcToDest.multiply(srcToPatient);
        }
        
        //loop through verts of roi2.sequence and if none of the verts is outside the bb, return false
        if(roi2.sequence.someVertex((vertex: vec3): boolean => {
            return vertex[0] < bb1.min![0] || vertex[0] > bb1.max![0] ||
            vertex[1] < bb1.min![1] || vertex[1] > bb1.max![1] ||
            vertex[2] < bb1.min![2] || vertex[2] > bb1.max![2];
        }, srcToDest)){
            const normal = roi1.sequence.getNormal() || vec3[2]Axis.applyTransformation(roi1.texture.getNormalAlignedTransformation()).normalize();
            return this.optimalBoundingBox(normal, roi1, roi2);
        }
        return false;
    }*/

    /**
     *
     * @param roi1
     * @param roi2
     * @returns
     */
    /*public static intersectingROIs(roi1: RegionOfInterest, roi2: RegionOfInterest, normal: vec3, patientToDest?: Matrix4f): {rotation: Matrix3f, bb: BoundingBox} | undefined {
        let patientSpaceVertices: vec3[] = [];
        BoundingBox.intersectionPoints(roi1, roi2, (point: vec3): boolean => {
            patientSpaceVertices.push(point);
            return false;
        });
        
        if(patientSpaceVertices.length > 0){
            const bb = new BoundingBox();
            let planarVertices: number[][] = new Array<number[]>(patientSpaceVertices.length);
            const planarCentroid = vec3.fromValues();
            let index = 0;
            for(const vert of patientSpaceVertices){
                const normalPosition = vec3.dotProduct(normal, vert);
                const planarVertex = vec3.fromValues(normal).scale(-normalPosition).add(vert);
                planarVertices[index++] = planarVertex.getArray();
                planarCentroid.add(planarVertex);
            }
            planarCentroid.scale(1/planarVertices.length);
            for(let v of planarVertices){
                v[0] -= planarCentroid[0];
                v[1] -= planarCentroid[1];
                v[2] -= planarCentroid[2];
            }
            let matrix = new eig.Matrix(planarVertices).transpose();
            const svd = eig.Decompositions.svd(matrix, true);
            const U = svd.U;
            const row = vec3.fromValues(U.get(0, 0), U.get(1, 0), U.get(2, 0)).normalize();
            const column = vec3.fromValues(U.get(0, 1), U.get(1, 1), U.get(2, 1)).normalize();
            const svdNormal = vec3.fromValues(U.get(0, 2), U.get(1, 2), U.get(2, 2)).normalize();
            if(vec3.dotProduct(svdNormal, normal) < 0){
                svdNormal.invert();
            }
            const normalAlignedRotation = new Matrix3f(row[0], row[1], row[2],
            column[0], column[1], column[2],
            svdNormal[0], svdNormal[1], svdNormal[2]);
            let transformation: Matrix4f | Matrix3f = new Matrix3f(normalAlignedRotation).transpose();
            if(patientToDest){
                transformation = new Matrix4f(transformation).multiply(patientToDest);
            }
            for(const vert of patientSpaceVertices){
                bb.expandWithPoint(vec3.fromValues(vert).applyTransformation(transformation));
            }
            return {rotation: normalAlignedRotation, bb: bb};
        }
        return undefined;
    }*/

    /**
     * Method to apply rotation on {@link BoundingBox}.\
     * This will rotate every corner vertex and store the result in this.
     * @deprecated It's not adviced to use this. Use {@link RotatedBoundingBox} instead.
     * @param rotation The rotation to be applied
     */
    public applyRotation(rotation: ReadonlyMat3): BoundingBox {
        const c = vec3.create();
        if (this.min && this.max) {
            const bb = new BoundingBox();
            bb.expandWithPoint(vec3.transformMat3(c, this.min, rotation));
            bb.expandWithPoint(
                vec3.transformMat3(
                    c,
                    vec3.fromValues(this.min[0], this.min[1], this.max[2]),
                    rotation
                )
            );
            bb.expandWithPoint(
                vec3.transformMat3(
                    c,
                    vec3.fromValues(this.min[0], this.max[1], this.min[2]),
                    rotation
                )
            );
            bb.expandWithPoint(
                vec3.transformMat3(
                    c,
                    vec3.fromValues(this.min[0], this.max[1], this.max[2]),
                    rotation
                )
            );
            bb.expandWithPoint(
                vec3.transformMat3(
                    c,
                    vec3.fromValues(this.max[0], this.min[1], this.min[2]),
                    rotation
                )
            );
            bb.expandWithPoint(
                vec3.transformMat3(
                    c,
                    vec3.fromValues(this.max[0], this.min[1], this.max[2]),
                    rotation
                )
            );
            bb.expandWithPoint(
                vec3.transformMat3(
                    c,
                    vec3.fromValues(this.max[0], this.max[1], this.min[2]),
                    rotation
                )
            );
            bb.expandWithPoint(vec3.transformMat3(c, this.max, rotation));
            this.set(bb);
        } else {
            console.warn('Attempt to rotate bb with no content');
        }
        return this;
    }

    /*protected getEdges(param: ReadonlyMat3 | ReadonlyMat4): Edge<vec3>[] {
        /*vertices as:
        0: {this.min},
        1: {this.max[0], this.min[1], this.min[2]},
        2: {this.min[0], this.max[1], this.min[2]},
        3: {this.max[0], this.max[1], this.min[2]},
        4: {this.min[0], this.min[1], this.max[2]},
        5: {this.max[0], this.min[1], this.max[2]},
        6: {this.min[0], this.max[1], this.max[2]},
        7: {this.max}*/
    /*const vertices = this.getVertices(param);
        //12 edges as [4 bottom edges, 4 vertical edges, 4 top edges]
        return [new Edge(vertices[0], vertices[1]),
            new Edge(vertices[1], vertices[3]),
            new Edge(vertices[3], vertices[2]),
            new Edge(vertices[2], vertices[0]),
            new Edge(vertices[0], vertices[4]),
            new Edge(vertices[1], vertices[5]),
            new Edge(vertices[2], vertices[6]),
            new Edge(vertices[3], vertices[7]),
            new Edge(vertices[4], vertices[5]),
            new Edge(vertices[5], vertices[7]),
            new Edge(vertices[7], vertices[6]),
            new Edge(vertices[6], vertices[4])];
    }*/
}
